import ContainerWrapper from "@components/BasicComponents/Wrappers/ContainerWrapper/ContainerWrapper"
import Cookies from "js-cookie"
import {useRouter} from "next/router"
import React, {useEffect, useRef, useState} from "react"

import styles from "./RebrandingNoticeBanner.module.scss"
import DecorativeDots from "./decorative-dots.svg"

const REBRAND_COOKIE = "rebrandNoticeSeen"
const REBRAND_POST =
	"/inspiration/news-archive/dream-broker-is-now-one-communications"
const BANNER_HEIGHT_VAR = "--rebrand-banner-height"

type Copy = {
	lineOne: string
	lineTwo: string
	message: string
	brand: string
	knowMore: string
	close: string
}

const copyByLocale: Record<string, Copy> = {
	en: {
		lineOne: "We have",
		lineTwo: "rebranded",
		message: "Dream Broker is now",
		brand: "One Communications",
		knowMore: "Learn more about it",
		close: "Close",
	},
	fi: {
		lineOne: "Olemme",
		lineTwo: "uudistuneet",
		message: "Dream Broker on nyt",
		brand: "One Communications",
		knowMore: "Lue lisää",
		close: "Sulje",
	},
	se: {
		lineOne: "Vi har",
		lineTwo: "nytt namn",
		message: "Dream Broker är nu",
		brand: "One Communications",
		knowMore: "Läs mer",
		close: "Stäng",
	},
	da: {
		lineOne: "Vi har",
		lineTwo: "nyt navn",
		message: "Dream Broker er nu",
		brand: "One Communications",
		knowMore: "Læs mere",
		close: "Luk",
	},
}

const rememberRebrandNotice = () => {
	const expirationDate = new Date()
	expirationDate.setMonth(expirationDate.getMonth() + 3)
	Cookies.set(REBRAND_COOKIE, "1", {expires: expirationDate, path: "/"})
}

const publishBannerOffset = (banner: HTMLElement) => {
	const height = `${banner.offsetHeight}px`
	const current =
		document.documentElement.style.getPropertyValue(BANNER_HEIGHT_VAR)
	if (current === height) return
	document.documentElement.style.setProperty(BANNER_HEIGHT_VAR, height)
}

const clearBannerOffset = () => {
	document.documentElement.style.removeProperty(BANNER_HEIGHT_VAR)
}

const RebrandingNoticeBanner = () => {
	const router = useRouter()
	const bannerRef = useRef<HTMLDivElement>(null)
	const [visible, setVisible] = useState(false)
	const copy = copyByLocale[router.locale || "en"] ?? copyByLocale.en

	useEffect(() => {
		setVisible(!Cookies.get(REBRAND_COOKIE))
	}, [])

	useEffect(() => {
		const banner = bannerRef.current
		if (!visible || !banner) {
			clearBannerOffset()
			return undefined
		}

		const publish = () => publishBannerOffset(banner)
		publish()
		const observer = new ResizeObserver(publish)
		observer.observe(banner)

		return () => {
			observer.disconnect()
			clearBannerOffset()
		}
	}, [visible])

	if (!visible) return null

	const dismiss = () => {
		rememberRebrandNotice()
		setVisible(false)
	}

	const knowMore = () => {
		void router.push(REBRAND_POST)
	}

	return (
		<div
			aria-label={copy.message}
			className={styles.banner}
			data-nosnippet={"true"}
			ref={bannerRef}
			role={"region"}
		>
			<ContainerWrapper className={styles.bannerRow}>
				<div className={styles.inner}>
					<div className={styles.mark}>
						<span className={styles.markText}>
							<span className={styles.lineOne}>
								{copy.lineOne}
								<DecorativeDots
									aria-hidden={"true"}
									className={styles.dots}
								/>
							</span>
							<span>{copy.lineTwo}</span>
						</span>
					</div>
					<p className={styles.message}>
						{copy.message} <strong>{copy.brand}</strong>
					</p>
					<div className={styles.actions}>
						<button
							className={styles.knowMore}
							type={"button"}
							onClick={knowMore}
						>
							{copy.knowMore}
						</button>
						<button
							aria-label={copy.close}
							className={styles.close}
							type={"button"}
							onClick={dismiss}
						>
							<svg aria-hidden={"true"} viewBox={"0 0 16 16"}>
								<path
									d={"M3.2 3.2l9.6 9.6M12.8 3.2l-9.6 9.6"}
									fill={"none"}
									stroke={"currentColor"}
									strokeLinecap={"round"}
									strokeWidth={"1.4"}
								/>
							</svg>
						</button>
					</div>
				</div>
			</ContainerWrapper>
		</div>
	)
}

export default RebrandingNoticeBanner
